import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Paper, Typography, Box, Divider } from '@mui/material';
import { useCountry } from '../../context/CountryContext';

const MALE_COLOR = '#0056ff';
const FEMALE_COLOR = '#ff4739';

const DivergingBar: React.FC<{ value: number }> = ({ value }) => {
    // Cap value at +/- 100 for visualization
    const cappedValue = Math.max(-100, Math.min(100, value));
    const isPositive = cappedValue >= 0;
    const barWidth = Math.abs(cappedValue) / 2; // Map 100% to 50% width

    return (
        <Box sx={{ position: 'relative', width: '100%', height: 24, display: 'flex', alignItems: 'center' }}>
            {/* Background track */}
            <Box sx={{ position: 'absolute', width: '100%', height: 4, bgcolor: '#e0e0e0', borderRadius: 2 }} />

            {/* Center marker */}
            <Box sx={{ position: 'absolute', left: '50%', height: 12, width: 2, bgcolor: '#bdbdbd', top: 6, transform: 'translateX(-50%)' }} />

            {/* The Bar */}
            <Box sx={{
                position: 'absolute',
                left: isPositive ? '50%' : `calc(50% - ${barWidth}%)`,
                width: `${barWidth}%`,
                height: 8,
                bgcolor: isPositive ? MALE_COLOR : FEMALE_COLOR,
                borderRadius: 1,
                transition: 'all 0.5s ease-in-out'
            }} />

            {/* Value Label */}
            <Typography variant="caption" sx={{
                position: 'absolute',
                left: isPositive ? `calc(50% + ${barWidth}% + 8px)` : 'auto',
                right: isPositive ? 'auto' : `calc(50% + ${barWidth}% + 8px)`,
                fontWeight: 'bold',
                color: isPositive ? MALE_COLOR : FEMALE_COLOR,
                transition: 'all 0.5s ease-in-out'
            }}>
                {value.toFixed(1)}%
            </Typography>
        </Box>
    );
};

const MetricItem: React.FC<{ title: string; value: string | number | null; isPercentage?: boolean; isScore?: boolean; isGap?: boolean; maxScore?: number }> = ({ title, value, isPercentage = false, isScore = false, isGap = false, maxScore = 100 }) => {
    const displayValue = typeof value === 'number' ? `${value.toFixed(2)}${isPercentage ? '%' : isScore ? `/${maxScore}` : ''}` : (value ?? 'N/A');

    if (isGap && typeof value === 'number') {
        return (
            <Box sx={{ py: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
                <DivergingBar value={value} />
            </Box>
        );
    }

    // Standard Bar for non-gap percentages (like Bonus Received) or Scores
    const barWidth = typeof value === 'number' ? (isScore ? (value / maxScore * 100) : (isPercentage ? Math.min(100, Math.abs(value)) : 0)) : 0;

    // Determine color for standard bars
    let barColor = '#1976D2';
    if (title.includes('Male')) barColor = MALE_COLOR;
    if (title.includes('Female')) barColor = FEMALE_COLOR;
    if (isScore) {
        if (typeof value === 'number') {
            if (value >= 80) barColor = '#4CAF50';
            else if (value >= 60) barColor = '#8BC34A';
            else if (value >= 40) barColor = '#FFC107';
            else barColor = '#FF9800';
        }
    }

    return (
        <Box sx={{ py: 1, borderBottom: '1px solid #eee' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">{title}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {displayValue}
                </Typography>
            </Box>
            {(isPercentage || isScore) && typeof value === 'number' && (barWidth > 0) && (
                <Box sx={{ width: '100%', height: 8, backgroundColor: '#e0e0e0', borderRadius: 1, mt: 0.5 }}>
                    <Box sx={{
                        width: `${barWidth}%`,
                        height: '100%',
                        backgroundColor: barColor,
                        borderRadius: 1,
                        transition: 'width 0.5s ease-in-out'
                    }} />
                </Box>
            )}
        </Box>
    );
};

const CompanyRawMetrics: React.FC<{ company: any; selectedYear?: number | null }> = ({ company, selectedYear }) => {
    const { country } = useCountry();
    const [isContentVisible, setIsContentVisible] = useState(true);

    const getMostRecentYearWithData = (startYear: number, endYear: number, field: string) => {
        for (let year = startYear; year >= endYear; year--) {
            if (company[`${year}_${field}`] !== null && company[`${year}_${field}`] !== undefined) {
                return year;
            }
        }
        return null;
    };

    const isFrance = country === 'fr';
    const recentYear = isFrance
        ? getMostRecentYearWithData(2024, 2018, 'CompositeIndex')
        : getMostRecentYearWithData(2024, 2017, 'DiffMeanHourlyPercent');
    const selectedYearField = isFrance ? 'CompositeIndex' : 'DiffMeanHourlyPercent';
    const hasSelectedYearData = selectedYear !== null && selectedYear !== undefined &&
        company[`${selectedYear}_${selectedYearField}`] !== null && company[`${selectedYear}_${selectedYearField}`] !== undefined;
    const activeYear = hasSelectedYearData ? selectedYear : recentYear;

    useEffect(() => {
        if (!activeYear) return;
        setIsContentVisible(false);
        const timer = window.setTimeout(() => setIsContentVisible(true), 80);
        return () => window.clearTimeout(timer);
    }, [activeYear]);

    // For France, show CompositeIndex components
    if (isFrance) {
        if (!activeYear) {
            return (
                <Paper elevation={3} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>Index Égalité Professionnelle</Typography>
                    <Typography>No recent equality index data available for this company.</Typography>
                </Paper>
            );
        }

        return (
            <Paper elevation={3} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Index Égalité Professionnelle ({activeYear})</Typography>
                <Box sx={{ opacity: isContentVisible ? 1 : 0, transition: 'opacity 220ms ease-in-out' }}>
                    <Box sx={{ mb: 2 }}>
                        <MetricItem
                            title="Composite Index"
                            value={company[`${activeYear}_CompositeIndex`]}
                            isScore
                        />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Component Scores</Typography>
                    <MetricItem
                        title="Pay Gap Score"
                        value={company[`${activeYear}_Note Ecart rémunération`]}
                        isScore
                        maxScore={40}
                    />
                    <MetricItem
                        title="Raise Rate Score"
                        value={company[`${activeYear}_Note Ecart taux d'augmentation`]}
                        isScore
                    />
                    <MetricItem
                        title="High Earners Score"
                        value={company[`${activeYear}_Note Hautes rémunérations`]}
                        isScore
                        maxScore={10}
                    />
                </Box>
            </Paper>
        );
    }

    // For UK and Ireland, show pay gap metrics
    if (!activeYear) {
        return (
            <Paper elevation={3} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Raw Pay Gap Metrics</Typography>
                <Typography>No recent pay gap data available for this company.</Typography>
            </Paper>
        );
    }

    return (
        <Paper elevation={3} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Raw Pay Gap Metrics ({activeYear})</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: FEMALE_COLOR, fontWeight: 'bold' }}>← Favors Women</Typography>
                    <Typography variant="caption" sx={{ color: MALE_COLOR, fontWeight: 'bold' }}>Favors Men →</Typography>
                </Box>
            </Box>

            <Box sx={{ opacity: isContentVisible ? 1 : 0, transition: 'opacity 220ms ease-in-out' }}>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Base Pay</Typography>
                        <MetricItem title="Mean Hourly Gap" value={company[`${activeYear}_DiffMeanHourlyPercent`]} isGap />
                        <MetricItem title="Median Hourly Gap" value={company[`${activeYear}_DiffMedianHourlyPercent`]} isGap />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Bonus Pay</Typography>
                        <MetricItem title="Mean Bonus Gap" value={company[`${activeYear}_DiffMeanBonusPercent`]} isGap />
                        <MetricItem title="Median Bonus Gap" value={company[`${activeYear}_DiffMedianBonusPercent`]} isGap />
                    </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>% Receiving a Bonus</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <MetricItem title="Male" value={company[`${activeYear}_MaleBonusPercent`]} isPercentage />
                    </Grid>
                    <Grid item xs={6}>
                        <MetricItem title="Female" value={company[`${activeYear}_FemaleBonusPercent`]} isPercentage />
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    );
};

export default CompanyRawMetrics;
